/**
 * Call Status Service
 * Serviço layer para operações com Supabase relacionadas a call status
 */

import { supabase } from '@/lib/supabase'
import type { CallStatus, CallStatusWithUser } from '@/types/call-status.types'
import logger from '@/utils/logger'

/**
 * Get current user's call status
 */
export async function getCurrentUserCallStatus(userId: string): Promise<CallStatus | null> {
  try {
    const { data, error } = await supabase
      .from('call_status')
      .select('*')
      .eq('user_id', userId)
      .single()

    if (error) {
      // PGRST116 = not found (expected when user has no call status)
      if (error.code === 'PGRST116') {
        return null
      }
      logger.error('[Call Status Service] Error fetching status:', error)
      throw new Error(`Erro ao buscar status: ${error instanceof Error ? error.message : "Erro desconhecido"}`)
    }

    return data as CallStatus
  } catch (err: unknown) {
    logger.error('[Call Status Service] Unexpected error in getCurrentUserCallStatus:', err)
    throw err
  }
}

/**
 * Get all active call statuses with user info
 */
export async function getActiveCallStatuses(): Promise<CallStatusWithUser[]> {
  try {
    const { data, error } = await supabase
      .from('call_status')
      .select(
        `
        *,
        user:profiles(
          id,
          full_name,
          email,
          role
        )
      `
      )
      .eq('is_active', true)
      .order('started_at', { ascending: false })

    if (error) {
      logger.error('[Call Status Service] Error fetching active statuses:', error)
      throw new Error(`Erro ao buscar calls ativos: ${error.message ?? "Erro desconhecido"}`)
    }

    return (data || []) as unknown as CallStatusWithUser[]
  } catch (err: unknown) {
    logger.error('[Call Status Service] Unexpected error in getActiveCallStatuses:', err)
    throw err
  }
}

/**
 * Activate call status for current user
 */
export async function activateCallStatus(userId: string): Promise<CallStatus> {
  try {
    const now = new Date().toISOString()

    const { data, error } = await supabase
      .from('call_status')
      .upsert(
        {
          user_id: userId,
          is_active: true,
          started_at: now,
          last_activity_at: now
        },
        {
          onConflict: 'user_id'
        }
      )
      .select()
      .single()

    if (error) {
      logger.error('[Call Status Service] Error activating status:', error)
      throw new Error(`Erro ao ativar status: ${error instanceof Error ? error.message : "Erro desconhecido"}`)
    }

    logger.debug('[Call Status Service] ✅ Activated for user:', userId)
    return data as CallStatus
  } catch (err: unknown) {
    logger.error('[Call Status Service] Unexpected error in activateCallStatus:', err)
    throw err
  }
}

/**
 * Deactivate call status for current user
 */
export async function deactivateCallStatus(userId: string): Promise<void> {
  try {
    const { error } = await supabase
      .from('call_status')
      .update({
        is_active: false,
        started_at: null,
        last_activity_at: null
      })
      .eq('user_id', userId)

    if (error) {
      logger.error('[Call Status Service] Error deactivating status:', error)
      throw new Error(`Erro ao desativar status: ${error instanceof Error ? error.message : "Erro desconhecido"}`)
    }

    logger.debug('[Call Status Service] ✅ Deactivated for user:', userId)
  } catch (err: unknown) {
    logger.error('[Call Status Service] Unexpected error in deactivateCallStatus:', err)
    throw err
  }
}

/**
 * Update last activity timestamp (keep-alive)
 */
export async function updateCallActivity(userId: string): Promise<void> {
  try {
    const { error } = await supabase
      .from('call_status')
      .update({
        last_activity_at: new Date().toISOString()
      })
      .eq('user_id', userId)
      .eq('is_active', true)

    if (error) {
      logger.error('[Call Status Service] Error updating activity:', error)
      // Don't throw - this is a keep-alive, non-critical operation
    }
  } catch (err: unknown) {
    logger.error('[Call Status Service] Unexpected error in updateCallActivity:', err)
    // Don't throw - this is a keep-alive, non-critical operation
  }
}
